// One Platform fee on top of the price (9.2%): Meetmy's 5% and the 4.2% that covers Stripe.
// They were two lines until October 2026, so older orders carry a transactionFee too.
export const PLATFORM_FEE = 0.092;
export const MIN_PLATFORM_FEE = 0.98;
// Meetmy's own part of the Platform fee: ambassadors are paid half of it, never of the whole fee.
export const MEETMY_FEE = 0.05;

// Payout (withdrawal) fees deducted from a provider's balance on cash-out.
// Shared so the backend payout logic and the creator-terms disclosure agree.
export const STRIPE_PAYOUT_FEE = 0.02;
export const PAYONEER_PAYOUT_FEE = 0.04;

// Minimum withdrawal amounts, enforced by the backend payout endpoint and
// mirrored in the wallet UI.
export const STRIPE_MIN_WITHDRAWAL = 10;
export const PAYONEER_MIN_WITHDRAWAL = 100;

// Free withdrawals are a per-person arrangement, so the list IS the rule. It
// belonged to the ambassador programme until 2026-08-27, which made a private
// deal read as a published perk anyone could qualify for.
//
// By user id, never by username: a handle can be changed in profile settings
// and the vacated one is claimable by anyone, which would hand a stranger a 0%
// fee with nobody deciding anything. Ids are prod's; the comment is the name.
export const FREE_STRIPE_PAYOUT_USER_IDS = [
  'bef107c3-203a-450e-b109-54fd5376dad2', // fxthysq
  '0d10b0dc-9873-4b5c-a3dc-5e922e6e9f6c', // sam
];

// Payoneer is settled by hand (PRINCIPLES.md entry 2), so it is offered only to the
// people it was agreed with, not to every creator. By user id, for the reason above.
export const PAYONEER_PAYOUT_USER_IDS = [
  '81238a51-9f43-4e5a-b329-c2cbba69ff96', // anastasiav
];

export const canPayOutViaPayoneer = (opts: { userId?: string | null }): boolean =>
  !!opts.userId && PAYONEER_PAYOUT_USER_IDS.includes(opts.userId);

/**
 * What this particular Stripe withdrawal costs — the ONE definition, so the
 * wallet's estimate, the ledger's recorded fee and the terms page can't
 * disagree about what a person is charged.
 *
 * Free means free at any amount: a threshold would make the rate depend on the
 * number being typed. Payoneer keeps its rate: it is settled by hand and its
 * fee is a real cost.
 */
export const stripePayoutFeeRate = (opts: { userId?: string | null }): number =>
  opts.userId && FREE_STRIPE_PAYOUT_USER_IDS.includes(opts.userId) ? 0 : STRIPE_PAYOUT_FEE;

// The single timing promise the product makes on a withdrawal. Three readers, one owner:
// the wallet success screen and the confirmation email render the sentence via
// payoutTimingNote(); creator-terms interpolates the raw numbers into its own legal phrasing.
// Never hardcode a day count in copy.
export const STRIPE_PAYOUT_MAX_BUSINESS_DAYS = 3;
export const PAYONEER_PAYOUT_MAX_BUSINESS_DAYS = 14;

/**
 * How long the money takes, as a bare phrase — for a wallet ledger row that
 * already says "Arrives" and has no room for a sentence. The sentence below is
 * composed FROM it, so a ledger row and an email can never promise different
 * numbers, and nobody has to slice the sentence up with `.replace()`.
 */
export const payoutArrivalPhrase = (method: 'stripe' | 'payoneer'): string =>
  method === 'stripe'
    ? `${STRIPE_PAYOUT_MAX_BUSINESS_DAYS} business days`
    : `${PAYONEER_PAYOUT_MAX_BUSINESS_DAYS} business days`;

/** The sentence, not just the number: the email template can't branch (LMX has no conditionals). */
export const payoutTimingNote = (method: 'stripe' | 'payoneer'): string =>
  `You'll receive your payout within ${payoutArrivalPhrase(method)}.`;

// The single timing promise on a manual refund (Stripe's card-refund window back to the
// client's original payment method). Reader: the client-terms refund clause, pinned by a
// drift test. Never hardcode a day count in copy.
export const REFUND_ARRIVAL_MIN_BUSINESS_DAYS = 5;
export const REFUND_ARRIVAL_MAX_BUSINESS_DAYS = 10;

/** The window as a bare phrase, so every surface promises the same pair of numbers. */
export const refundArrivalPhrase = (): string =>
  `${REFUND_ARRIVAL_MIN_BUSINESS_DAYS}–${REFUND_ARRIVAL_MAX_BUSINESS_DAYS} business days`;

/**
 * The Platform fee added on top of the base price. Single source of truth shared
 * by the backend (checkout, tips) and the frontend (the totals a client sees).
 */
export const getPlatformFee = (amount: number): number =>
  amount <= 0 ? 0 : Math.max(Number((amount * PLATFORM_FEE).toFixed(2)), MIN_PLATFORM_FEE);

/** A rate as copy reads it: 0.092 → "9.2%". */
export const pct = (rate: number): string => `${Number((rate * 100).toFixed(2))}%`;
